/**
 * Donor-facing payment-provider marks for the checkout method picker.
 *
 * Before this module, `checkout.getProviderIcon` returned a hardcoded map of
 * generic monochrome stroke glyphs — a card outline for Stripe, a
 * circle-with-a-plus for Nagad, a square-with-a-plus for bKash — for 7 of the
 * ~22 registered payment providers, and an EMPTY string for everything else
 * (`offline`, `sslcommerz`, `paystation`, `alpha`, `sandbox` all rendered a
 * blank span). Donors got no brand recognition at the moment they choose how
 * to pay, which is exactly where recognition matters most in markets where
 * bKash/Nagad are identified by their colour before their name is read.
 *
 * INLINE, not fetched. checkout.js also runs on hosted donate pages, where a
 * build-emitted root-relative asset URL would resolve against the page origin
 * rather than the widget build origin. Inlining avoids that origin dependency,
 * removes a second request, and keeps the offline kiosk path self-contained.
 * The third-party embed itself runs in an iframe, so the host page's img-src
 * policy does not govern content inside the widget document.
 *
 * THREE KINDS OF MARK live here:
 *
 *   1. VECTOR ARTWORK (bKash, Nagad), inlined via `?raw`.
 *   2. RASTER ARTWORK (Alpha, PayStation, SSLCommerz), kept as compact copies
 *      of the corresponding dashboard assets, inlined via `?inline`, and
 *      wrapped in an SVG so all marks share one sizing contract.
 *   3. MONOGRAMS — a brand-coloured rounded square with a single letter, for
 *      providers whose verified artwork is not bundled here. These
 *      are deliberately NOT redraws of a registered trademark: bKash, Nagad,
 *      Stripe, PayPal et al. publish brand guidelines governing their real
 *      marks, so an approximation would be a licensing problem, not a design
 *      one. A letter in the brand's colour claims to be nothing but a letter.
 *
 * Vector artwork remains the preferred format. When only raster artwork is
 * available, keep its transparent background, downscale for the 36px row slot,
 * and palette-quantize before adding it to the bundle.
 *
 * Providers absent from BRAND_MARKS fall through to a neutral slate monogram
 * built from their display name. That is intentional: a wrong brand colour
 * reads as a broken integration, so a provider is added below only when its
 * hue is verifiable (repo-confirmed, or unambiguous vendor primary). Known
 * gaps, all currently neutral here: pawapay, jazzcash, xendit,
 * dlocal, moyasar, fawry, liqpay, easypaisa, przelewy24, iyzico.
 */

// Official vendor artwork, inlined at build time by Vite's `?raw`. Pure vector,
// no embedded raster, no <text> — so unlike the monograms below they carry no
// font dependency and render identically everywhere.
//
// ⚠ These copies also have CROPPED viewBoxes. The vendor exports are
// `viewBox="0 0 80 80"` with the artwork in roughly the middle third (bKash
// occupies x 8-71, y 25-54) — over half the box is empty padding. In a
// fixed-height slot that padding is what you get, so the mark rendered at
// about a third of its available height and read as a smudge. The boxes are
// cropped to the artwork bounds plus margin. Re-crop on any artwork refresh.
//
// ⚠ These copies are NAMESPACED and must stay that way. Both vendor files ship
// Illustrator's generic `.st0/.st1/.st2` class names inside an SVG <style>
// block, with DIFFERENT colours per file. An inline SVG's <style> is not scoped
// to that SVG — it applies document-wide — so shipping both verbatim would let
// whichever renders last repaint the other (bKash pink turning up in Nagad's
// mark). The copies under ./assets/payment-providers/ prefix classes per brand
// (`bk-`, `ng-`) and remove document-global ids/clip wrappers entirely. The
// latter matters when one provider mark renders more than once on a page.
// Re-run that sanitization if you refresh the artwork from the vendor.
import bkashMark from './assets/payment-providers/bkash.svg?raw';
import nagadMark from './assets/payment-providers/nagad.svg?raw';
// Byte-identical to the dashboard's manual-payment-icon.svg (bar the sizing
// attributes). `offline` and `manual` MUST render the same mark on the donation
// page and in Settings → Integrations; shipping two lookalike drawings is how
// they drifted before — a thin currentColor line glyph for donors and a filled
// slate square for admins, for the same payment method.
import manualPaymentMark from './assets/payment-providers/manual-payment.svg?raw';
// Byte-identical to the compact Alpha artwork shown on Settings →
// Integrations. Keep the copy inside this package because the production
// widget Docker build uses public/widget as its build context; `?inline` keeps
// the public checkout CSP-safe and request-free.
import alphaMarkData from './assets/payment-providers/alpha.png?inline';
import paystationMarkData from './assets/payment-providers/paystation.png?inline';
import sslcommerzMarkData from './assets/payment-providers/sslcommerz.png?inline';

/** Neutral fill for providers with no verified brand colour (slate-500). */
const NEUTRAL_FILL = '#64748B';

/**
 * Preserve a raster lockup's aspect ratio while returning the same inline-SVG
 * shape consumed by every existing call site.
 */
function rasterMark(
  dataUri: string,
  width: number,
  height: number,
  label: string,
): string {
  return (
    `<svg width="18" height="18" viewBox="0 0 ${width} ${height}" ` +
    'xmlns="http://www.w3.org/2000/svg">' +
    `<title>${label}</title>` +
    `<image href="${dataUri}" width="${width}" height="${height}"/>` +
    '</svg>'
  );
}

/**
 * Brand-coloured rounded square + white (or dark) monogram.
 *
 * `ink` exists for light fills — a white letter on Nagad orange or
 * Flutterwave amber is barely legible at 18px. The mark is decorative
 * (`aria-hidden` at the call site, and the provider name is rendered as text
 * beside it), but "decorative" is not a licence to be unreadable.
 */
function monogram(fill: string, letter: string, ink = '#FFFFFF'): string {
  return (
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none">' +
    `<rect width="24" height="24" rx="5" fill="${fill}"/>` +
    `<text x="12" y="12" text-anchor="middle" dominant-baseline="central" ` +
    `font-family="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" ` +
    `font-size="14" font-weight="700" fill="${ink}">${letter}</text>` +
    '</svg>'
  );
}

// Dimensions match the on-disk PNG headers. Both constants are shared between
// provider-key and server-declared icon lookups so the two paths cannot drift.
const ALPHA_MARK = rasterMark(alphaMarkData, 280, 120, 'Alpha Payment Gateway');
const PAYSTATION_MARK = rasterMark(paystationMarkData, 201, 108, 'PayStation');
const SSLCOMMERZ_MARK = rasterMark(sslcommerzMarkData, 448, 86, 'SSLCommerz');

/**
 * Official artwork where we have it; otherwise verified brand hues only.
 * Monogram colour sources:
 *   - stripe / paypal / razorpay / paystack / flutterwave → vendor primaries.
 */
const BRAND_MARKS: Record<string, string> = {
  // Official vendor artwork — the two methods that dominate donor choice in
  // Bangladesh, where the mark is recognised before the name is read.
  bkash: bkashMark,
  nagad: nagadMark,
  alpha: ALPHA_MARK,
  paystation: PAYSTATION_MARK,
  sslcommerz: SSLCOMMERZ_MARK,
  // Monograms, pending official artwork suitable for this bundle.
  stripe: monogram('#635BFF', 'S'),
  paypal: monogram('#003087', 'P'),
  razorpay: monogram('#3395FF', 'R'),
  paystack: monogram('#011B33', 'P'),
  flutterwave: monogram('#F5A623', 'F', '#1A1A1A'),
};

/**
 * Non-brand methods — not anyone's trademark, so no brand colour applies.
 *
 * `offline` is the donor-facing bKash/Nagad/bank-transfer method
 * (CHG-MANUALPAY). It aggregates several real brands under one row, so a
 * neutral banknote mark is more honest than any single vendor's colour.
 *
 * `offline` and `manual` both use the SHARED artwork imported above, which is
 * the same file the dashboard renders. That is the point: the method used to
 * show a thin currentColor line glyph to donors and a filled slate square to
 * admins, so "manual payment" looked like two different things depending on
 * where you stood. One mark, one file, both surfaces.
 *
 * The trade-off, accepted deliberately: the shared mark carries its own fills,
 * so it no longer picks up the `.dk-active` theme tint the old glyph did.
 * Cross-surface consistency beats per-theme tinting for a payment mark, which
 * donors read as an identity rather than as chrome.
 *
 * A multi-brand accepted-methods banner is deliberately not used here: it
 * cannot represent the identity of one row and may claim methods the current
 * organization has not configured.
 */
const FUNCTIONAL_MARKS: Record<string, string> = {
  offline: manualPaymentMark,
  manual: manualPaymentMark,
  // manual_payment_accounts.method values, for the offline account card.
  // `bkash` / `nagad` are NOT listed: they already resolve to real vendor
  // artwork via BRAND_MARKS, which is the point. These three have no artwork of
  // their own, so they share the same banknote mark the offline method uses.
  'bank-transfer': manualPaymentMark,
  'mobile-wallet': manualPaymentMark,
  other: manualPaymentMark,
  // Flask — test mode. Reads as "not real money" at a glance.
  sandbox:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none">' +
    '<path d="M9 3v6l-4.5 8A2 2 0 0 0 6.3 20h11.4a2 2 0 0 0 1.8-3L15 9V3" ' +
    'stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' +
    '<path d="M8 3h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '</svg>',
};

/**
 * Marks keyed by the CANONICAL ICON FILENAME STEM — i.e. `ADAPTER_METADATA.icon`
 * minus its extension, as delivered on `PaymentMethodEntry.icon`.
 *
 * This is the spec's source of truth for the row mark:
 *   ref:tier-5-experience/widget-payment-step picker_ux.rendering —
 *   "Each row carries the method's icon (per ADAPTER_METADATA.icon)".
 *
 * Keying here rather than on `provider_key` means the server decides WHICH mark
 * a method gets, so a registry change (e.g. pointing a new aggregator at an
 * existing icon) needs no widget release. The stem is matched extension-free
 * because the registry declares `.svg` for every provider while some assets are
 * raster — the same normalisation the dashboard resolver uses.
 *
 * `manual-payment-icon` is shared by BOTH `manual` and `offline` in the
 * registry, and both correctly want the banknote glyph here.
 */
const MARKS_BY_ICON_STEM: Record<string, string> = {
  'bkash-logo': bkashMark,
  'nagad-logo': nagadMark,
  'alpha-logo': ALPHA_MARK,
  'paystation-logo': PAYSTATION_MARK,
  'sslcommerz-logo': SSLCOMMERZ_MARK,
  'stripe-logo': monogram('#635BFF', 'S'),
  'paypal-logo': monogram('#003087', 'P'),
  'razorpay-logo': monogram('#3395FF', 'R'),
  'paystack-logo': monogram('#011B33', 'P'),
  'flutterwave-logo': monogram('#F5A623', 'F', '#1A1A1A'),
};

/** `<name>.svg` -> `<name>`. Registry filenames are always extensioned. */
function iconStem(icon: string): string {
  return icon.replace(/\.[^.]+$/, '');
}

/** First character suitable for a monogram, uppercased. */
function initial(providerKey: string, displayName?: string): string {
  const source = (displayName || '').trim() || providerKey;
  // Escape the two characters that could break out of the SVG text node. The
  // source is a server-provided display name, so it is not attacker-controlled
  // in practice, but this string is injected via innerHTML.
  const char = source.charAt(0).toUpperCase();
  return char === '<' || char === '&' ? '?' : char;
}

/**
 * Resolve the inline SVG mark for one payment method.
 *
 * Resolution order, and why:
 *   1. `icon` — the server-declared ADAPTER_METADATA filename off
 *      PaymentMethodEntry. The spec's source of truth, so it wins.
 *   2. `provider_key` — fallback for providers whose registry `icon` is null
 *      (`sandbox`) and for the non-brand glyphs (`offline` / `manual`), and a
 *      safety net if a response predates the field being populated.
 *   3. Neutral monogram off the display name.
 *
 * Never returns an empty string — an unrecognised provider still gets a
 * neutral monogram, so the picker rows stay visually aligned instead of one
 * row losing its icon column.
 */
export function providerMark(
  providerKey: string,
  displayName?: string,
  icon?: string | null,
): string {
  return (
    (icon ? MARKS_BY_ICON_STEM[iconStem(icon)] : undefined) ||
    BRAND_MARKS[providerKey] ||
    FUNCTIONAL_MARKS[providerKey] ||
    monogram(NEUTRAL_FILL, initial(providerKey, displayName))
  );
}

/** Provider keys carrying a verified brand mark (exported for tests). */
export const BRANDED_PROVIDER_KEYS = Object.keys(BRAND_MARKS);

/** Icon stems the registry can resolve (exported for tests). */
export const BRANDED_ICON_STEMS = Object.keys(MARKS_BY_ICON_STEM);
